import { useEffect, useState, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Button, Input, Typography, Dropdown, Avatar, Modal, message, Tag, Divider,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, FolderOpenOutlined, SettingOutlined,
  UserOutlined, CreditCardOutlined, MessageOutlined,
  EditOutlined, DeleteOutlined, MoreOutlined,
} from '@ant-design/icons';
import { useAppStore } from '@/stores/useAppStore';
import IdentityBadge from '@/components/IdentityBadge';
import LevelBadge from '@/components/LevelBadge';
import WechatQrCode from '@/components/WechatQrCode';
import type { IdentityType } from '@/types/user';
import { calculateUserLevel, getLevelStyle } from '@/utils/level';
import {
  APP_NAME,
  LOGO_URL,
  LOGO_ALT,
  BUTTON_TEXT,
  MESSAGE_TEXT,
  CONFIRM_DIALOG,
} from '@/constants/app';

const { Text, Title } = Typography;

const formatTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return time;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `昨天 ${time}`;
  return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' }) + ' ' + time;
};

const getDateGroup = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return '今天';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return '昨天';
  const diff = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (diff <= 7) return '最近';
  return '更早';
};

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    authToken, loadConfigFromServer, fetchCreditBalance,
    conversations, currentConversationId, createConversation,
    setCurrentConversation, loadConversation, deleteConversation,
    renameConversation, authUser, creditBalance,
  } = useAppStore();

  const [searchText, setSearchText] = useState('');
  const [renameId, setRenameId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [badgeModalVisible, setBadgeModalVisible] = useState(false);

  useEffect(() => {
    if (authToken) {
      loadConfigFromServer();
      fetchCreditBalance();
    }
  }, [authToken, loadConfigFromServer, fetchCreditBalance]);

  const handleNewChat = useCallback(() => {
    setCurrentConversation(null);
    navigate('/');
  }, [setCurrentConversation, navigate]);

  const handleSelectConversation = useCallback(async (id: number) => {
    await loadConversation(id);
    navigate('/');
  }, [loadConversation, navigate]);

  const handleDeleteConversation = useCallback((id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    Modal.confirm({
      title: CONFIRM_DIALOG.DELETE_CONVERSATION_TITLE,
      content: CONFIRM_DIALOG.DELETE_CONVERSATION_CONTENT,
      okText: BUTTON_TEXT.DELETE,
      cancelText: BUTTON_TEXT.CANCEL,
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteConversation(id);
        message.success(MESSAGE_TEXT.DELETE_SUCCESS);
      },
    });
  }, [deleteConversation]);

  const handleStartRename = useCallback((id: number, title: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setRenameId(id);
    setRenameValue(title);
  }, []);

  const handleConfirmRename = useCallback(async () => {
    if (renameId && renameValue.trim()) {
      await renameConversation(renameId, renameValue.trim());
    }
    setRenameId(null);
  }, [renameId, renameValue, renameConversation]);



  const filteredConversations = conversations.filter((c) =>
    !searchText || c.title.toLowerCase().includes(searchText.toLowerCase())
  );

  const groupedConversations = filteredConversations.reduce((groups, conv) => {
    const group = getDateGroup(conv.lastMessageAt || conv.updatedAt);
    if (!groups[group]) groups[group] = [];
    groups[group].push(conv);
    return groups;
  }, {} as Record<string, typeof conversations>);

  const currentPath = location.pathname;

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/' || currentPath.startsWith('/chat');
    return currentPath.startsWith(path);
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#fff' }}>
      {/* 左侧固定导航栏 */}
      <div style={{
        width: 260, display: 'flex', flexDirection: 'column', flexShrink: 0,
        background: '#f9fafb', borderRight: '1px solid #e5e7eb',
      }}>
        {/* Logo */}
        <div style={{ padding: '16px 14px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src={LOGO_URL}
            alt={LOGO_ALT}
            style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
          />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#111827', letterSpacing: -0.3 }}>{APP_NAME}</span>
        </div>

        {/* 新建对话按钮 */}
        <div style={{ padding: '4px 14px 12px' }}>
          <Button block icon={<PlusOutlined />} onClick={handleNewChat}
            style={{
              background: '#FF3F38', borderColor: '#FF3F38',
              color: '#fff', borderRadius: 10, height: 36, fontSize: 13, fontWeight: 500,
            }}>
            {BUTTON_TEXT.NEW_CHAT}
          </Button>
        </div>

        {/* 搜索对话 */}
        <div style={{ padding: '0 14px 8px' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#9ca3af', fontSize: 13 }} />}
            placeholder="搜索对话..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="small"
            style={{ borderRadius: 8, height: 32 }}
          />
        </div>

        {/* 对话列表 */}
        <div style={{ flex: 1, overflow: 'auto', paddingBottom: 8 }}>
          {filteredConversations.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Text style={{ fontSize: 13, color: '#9ca3af' }}>
                {searchText ? '没有匹配的对话' : '暂无对话'}
              </Text>
            </div>
          ) : (
            Object.entries(groupedConversations).map(([group, convs]) => (
              <div key={group}>
                <div style={{ padding: '10px 20px 4px', fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>{group}</div>
                {convs.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv.id)}
                    style={{
                      padding: '8px 14px', margin: '1px 8px', borderRadius: 8, cursor: 'pointer',
                      background: currentConversationId === conv.id && currentPath === '/' ? '#fff1f0' : 'transparent',
                      display: 'flex', alignItems: 'center', gap: 10, transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!(currentConversationId === conv.id && currentPath === '/')) {
                        e.currentTarget.style.background = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = (currentConversationId === conv.id && currentPath === '/') ? '#fff1f0' : 'transparent';
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {renameId === conv.id ? (
                        <Input
                          size="small"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onPressEnter={handleConfirmRename}
                          onBlur={handleConfirmRename}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <Text ellipsis style={{
                          fontSize: 13, display: 'block',
                          color: (currentConversationId === conv.id && currentPath === '/') ? '#FF3F38' : '#374151',
                          fontWeight: (currentConversationId === conv.id && currentPath === '/') ? 500 : 400,
                        }}>{conv.title}</Text>
                      )}
                    </div>
                    {renameId !== conv.id && (
                      <Dropdown trigger={['click']} menu={{
                        items: [
                          { key: 'rename', icon: <EditOutlined />, label: '重命名', onClick: ({ domEvent }) => handleStartRename(conv.id, conv.title, domEvent as any) },
                          { key: 'delete', icon: <DeleteOutlined />, label: BUTTON_TEXT.DELETE, danger: true, onClick: ({ domEvent }) => handleDeleteConversation(conv.id, domEvent as any) },
                        ],
                      }}>
                        <Button
                          type="text"
                          size="small"
                          icon={<MoreOutlined style={{ color: '#9ca3af' }} />}
                          onClick={(e) => e.stopPropagation()}
                          style={{ opacity: 0, width: 24, height: 24 }}
                          className="conv-action-btn"
                        />
                      </Dropdown>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* 底部导航菜单 */}
        <div style={{
          borderTop: '1px solid #e5e7eb',
          padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {/* 文件库 */}
          <div
            onClick={() => navigate('/files')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
              background: isActive('/files') ? '#fff1f0' : 'transparent',
            }}
            onMouseEnter={(e) => { if (!isActive('/files')) e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = isActive('/files') ? '#fff1f0' : 'transparent'; }}
          >
            <FolderOpenOutlined style={{ fontSize: 15, color: isActive('/files') ? '#FF3F38' : '#6b7280' }} />
            <span style={{ fontSize: 13, color: isActive('/files') ? '#FF3F38' : '#374151', fontWeight: isActive('/files') ? 500 : 400 }}>文件库</span>
          </div>

          {/* 积分 - 内测暂不开放 */}
          <div
            onClick={() => message.info('内测暂不开放')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
              background: 'transparent',
              opacity: 0.7,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <CreditCardOutlined style={{ fontSize: 15, color: '#6b7280' }} />
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 400 }}>积分</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af', background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>
              {creditBalance ?? 0} 积分
            </span>
          </div>

          {/* 反馈 */}
          <div
            onClick={() => navigate('/feedback')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
              background: isActive('/feedback') ? '#fff1f0' : 'transparent',
            }}
            onMouseEnter={(e) => { if (!isActive('/feedback')) e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = isActive('/feedback') ? '#fff1f0' : 'transparent'; }}
          >
            <MessageOutlined style={{ fontSize: 15, color: isActive('/feedback') ? '#FF3F38' : '#6b7280' }} />
            <span style={{ fontSize: 13, color: isActive('/feedback') ? '#FF3F38' : '#374151', fontWeight: isActive('/feedback') ? 500 : 400 }}>反馈</span>
          </div>

          {/* 设置 */}
          <div
            onClick={() => navigate('/settings')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
              background: isActive('/settings') ? '#fff1f0' : 'transparent',
            }}
            onMouseEnter={(e) => { if (!isActive('/settings')) e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = isActive('/settings') ? '#fff1f0' : 'transparent'; }}
          >
            <SettingOutlined style={{ fontSize: 15, color: isActive('/settings') ? '#FF3F38' : '#6b7280' }} />
            <span style={{ fontSize: 13, color: isActive('/settings') ? '#FF3F38' : '#374151', fontWeight: isActive('/settings') ? 500 : 400 }}>设置</span>
          </div>

          {/* 用户信息 - 头像占两行，右侧是用户名和身份徽章 */}
          <div
            style={{
              display: 'flex', gap: 10, padding: '8px 10px',
              borderRadius: 8, width: '100%', boxSizing: 'border-box',
            }}
          >
            {/* 左侧：头像（占两行高度） */}
            <Avatar
              size={40}
              src={authUser?.avatarUrl}
              icon={!authUser?.avatarUrl && <UserOutlined />}
              style={{ background: '#fee2e2', color: '#FF3F38', fontSize: 16, flexShrink: 0 }}
            />
            {/* 右侧：用户名 + 身份徽章 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0, justifyContent: 'center' }}>
              {/* 用户名 */}
              <span
                style={{
                  fontSize: 13,
                  color: '#374151',
                  fontWeight: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={authUser?.displayName || authUser?.username || '用户'}
              >
                {authUser?.displayName || authUser?.username || '用户'}
              </span>
              {/* 身份徽章或等级令牌 - 居左对齐，不拉伸 */}
              {(() => {
                const hasSpecialIdentity = (authUser?.identities || []).some(
                  (id: string) => id !== 'regular'
                );
                if (hasSpecialIdentity) {
                  // 有特殊身份，显示身份徽章
                  return (
                    <div
                      style={{ alignSelf: 'flex-start', cursor: 'pointer' }}
                      onClick={() => setBadgeModalVisible(true)}
                      title="点击查看徽章说明"
                    >
                      <IdentityBadge
                        identities={(authUser?.identities as IdentityType[]) || ['regular']}
                        isPermanentVip={authUser?.isPermanentVip}
                        size="small"
                      />
                    </div>
                  );
                }
                // 普通用户，显示等级令牌
                const totalRecharge = authUser?.totalRecharge || 0;
                return (
                  <div
                    style={{ alignSelf: 'flex-start', cursor: 'pointer' }}
                    onClick={() => setBadgeModalVisible(true)}
                    title="点击查看徽章说明"
                  >
                    <LevelBadge totalRecharge={totalRecharge} size="small" />
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* 右侧内容区域 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', position: 'relative' }}>
        {/* 右侧悬浮微信二维码 */}
        <WechatQrCode position="right" />
        <Outlet />
      </div>

      {/* 徽章说明弹窗 */}
      <Modal
        title="🎖️ 徽章系统说明"
        open={badgeModalVisible}
        onCancel={() => setBadgeModalVisible(false)}
        footer={null}
        width={700}
        maskClosable={false}
      >
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {/* 等级徽章说明 */}
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>📊 等级徽章</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              根据积分增长自动获得，展现您的产品之旅成长轨迹
            </Text>
            <div style={{ display: 'grid', gap: 8 }}>
              {[
                { level: 1, name: '产品助理', desc: '起点 - 种子破土而出，象征产品之路的开始，充满成长潜力' },
                { level: 2, name: '初级产品经理', desc: '书写 - 拿起铅笔，开始书写自己的产品故事' },
                { level: 3, name: '中级产品经理', desc: '创意 - 灵感闪现，用创意点亮产品方向' },
                { level: 4, name: '高级产品经理', desc: '突破 - 推动产品起飞，实现突破性增长' },
                { level: 5, name: '资深产品经理', desc: '守护 - 用经验守护产品，成为团队可靠支柱' },
                { level: 6, name: '产品专家', desc: '专业 - 获得专业认证，成为领域专家' },
                { level: 7, name: '产品总监', desc: '视野 - 高瞻远瞩，用战略眼光引领方向' },
                { level: 8, name: '首席产品官', desc: '卓越 - 如钻石般珍贵，达到职业巅峰' },
              ].map((item) => (
                <div
                  key={item.level}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Text strong>Lv.{item.level} {item.name}</Text>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      {item.desc}
                    </Text>
                  </div>
                  <LevelBadge totalRecharge={item.level === 1 ? 0 : item.level === 2 ? 10 : item.level === 3 ? 50 : item.level === 4 ? 100 : item.level === 5 ? 200 : item.level === 6 ? 500 : item.level === 7 ? 1000 : 2000} size="default" />
                </div>
              ))}
            </div>
          </div>

          <Divider />

          {/* 身份徽章说明 */}
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>🌟 身份徽章</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              授予具有特殊贡献或身份的用户，体现平台认可与感谢
            </Text>
            <div style={{ display: 'grid', gap: 8 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Text strong>超级产品经理</Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    尊贵 - 最珍贵的钻石，代表最高级别的用户身份
                  </Text>
                </div>
                <IdentityBadge identities={['super_pm']} isPermanentVip={true} size="default" />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Text strong>产品教练</Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    学识 - 博士帽代表持续学习和知识分享，是平台的智囊团
                  </Text>
                </div>
                <IdentityBadge identities={['coach']} size="default" />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Text strong>天使产品</Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    新生 - 通过邀请码加入的天使用户，拥有无限成长潜力
                  </Text>
                </div>
                <IdentityBadge identities={['angel_pm']} size="default" />
              </div>
            </div>
          </div>

          <Divider />

          {/* 设计说明 */}
          <div>
            <Title level={5}>🎨 设计理念</Title>
            <ul style={{ paddingLeft: 20, margin: 0, color: '#666' }}>
              <li>每个徽章都有独特的图标和寓意，体现产品文化</li>
              <li>等级徽章采用成长进阶主题，从种子到星辰</li>
              <li>所有徽章都有闪光动画效果，等级越高动画越快</li>
              <li>身份徽章体现特殊贡献，增强用户归属感</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* CSS for hover effect on conversation action buttons */}
      <style>{`
        .conv-action-btn {
          opacity: 0 !important;
          transition: opacity 0.15s;
        }
        div:hover > .conv-action-btn,
        div:hover .conv-action-btn {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
}
